import { useEffect, useRef } from 'react'
import classNames from 'classnames'

import { EMPTY_STRING } from '../../../constants/common'
import { useContainerWidth, useElementWidthResize } from '../../../hooks'
import {
  GRETA_PANEL_CHATLIST_DOCK_MIN_WIDTH,
  GRETA_PANEL_CHATLIST_MAX_WIDTH_RATIO,
  GRETA_PANEL_CHATLIST_MIN_WIDTH,
  GRETA_PANEL_COMPACT_ATTRIBUTE,
  GRETA_PANEL_STYLE_VARS,
  GRETA_PANEL_VISIBLE_ATTRIBUTE,
} from '../GretaAIChat.constants'
import { useGretaAIChatContext } from '../GretaAIChat.hooks'
import {
  getIsGretaAIChatDialogOpen,
  getIsGretaAIChatThinking,
} from '../GretaAIChat.utils'
import { getIsGretaAIChatProjectsView } from '../GretaAIChat.utils.projects'
import {
  getHasGretaAIChatSourceSwitched,
  getIsGretaAIChatFinanceSource,
  getIsGretaAIChatSourceChoiceShown,
} from '../GretaAIChat.utils.source'
import { GretaAIChatComposer } from '../GretaAIChatComposer'
import { GretaAIChatHeader } from '../GretaAIChatHeader'
import { GretaAIChatProjectsPage } from '../GretaAIChatProjectsPage'
import { GretaAIChatSourceSweep } from '../GretaAIChatSourceSweep'
import { GretaAIChatStripNotice } from '../GretaAIChatStripNotice'

import { GretaAIChatChatListColumn } from './GretaAIChatChatListColumn'
import { GretaAIChatChatListDrawer } from './GretaAIChatChatListDrawer'
import { useGretaAIChatPanel } from './GretaAIChatPanel.hooks'
import { GretaAIChatPanelChatAction } from './GretaAIChatPanelChatAction'
import { GretaAIChatPanelFeed } from './GretaAIChatPanelFeed'
import { GretaAIChatPanelProjectAction } from './GretaAIChatPanelProjectAction'

import * as styles from './GretaAIChatPanel.module.css'

function GretaAIChatPanel() {
  const { actions, conversation, panel, source } = useGretaAIChatContext()

  const {
    activeChatId,
    chatAction,
    chatListWidth,
    isChatListColumnOpen,
    isChatListHovered,
    isChatListOrderFrozen,
    isChatListPinned,
    isLimitReached,
    isLocked,
    isMinimized,
    mode,
    panelRef,
    projectAction,
    view,
  } = panel

  const { status } = conversation

  const {
    onChatListResize,
    onCloseChatList,
    onHoverChatList,
    onMinimize,
    onRetry,
    onSelectChat,
    onStop,
    onToggleMode,
    onUnhoverChatList,
  } = actions

  const {
    isPinned,
    isSourceChoiceEnabled,
    isSourceOutage,
    isSourceRevoked,
    source: chatSource,
    sourceSwitchCount,
  } = source

  const inputRef = useRef(null)
  const bodyRef = useRef(null)
  const chatListToggleRef = useRef(null)

  useEffect(() => {
    if (!isMinimized) {
      const input = inputRef.current

      input?.focus()
      input?.setSelectionRange(input.value.length, input.value.length)
    }
  }, [isMinimized])

  const isProjectsPageShown = !isLocked && getIsGretaAIChatProjectsView(view)
  const isGenerating = !isProjectsPageShown && getIsGretaAIChatThinking(status)

  const { handleKeyDown, handleToggleMode, isExpanded, isModeTransition } =
    useGretaAIChatPanel({
      isChatMenuOpen: isChatListOrderFrozen,
      isDialogOpen: getIsGretaAIChatDialogOpen({ chatAction, projectAction }),
      isGenerating,
      mode,
      onMinimize,
      onStop,
      onToggleMode,
    })

  const bodyWidth = useContainerWidth(bodyRef)

  const isChatListDocked =
    isExpanded &&
    (bodyWidth === null || bodyWidth >= GRETA_PANEL_CHATLIST_DOCK_MIN_WIDTH)

  const { elementProps: chatListProps, resizeHandleProps } =
    useElementWidthResize({
      containerRef: bodyRef,
      disabled: !isChatListDocked,
      getNextWidth: ({ deltaX, startWidth }) => startWidth + deltaX,
      initialWidth: chatListWidth,
      maxWidthRatioToContainer: GRETA_PANEL_CHATLIST_MAX_WIDTH_RATIO,
      minWidth: GRETA_PANEL_CHATLIST_MIN_WIDTH,
      onResizeEnd: ({ width }) => onChatListResize(width),
    })

  const panelClassName = classNames(styles.panel, {
    [styles.compact]: !isExpanded,
    [styles.expanded]: isExpanded,
    [styles.minimized]: isMinimized,
    [styles.modeTransition]: isModeTransition,
  })

  const isFinanceSource = getIsGretaAIChatFinanceSource(chatSource)

  const hasSourceSwitched = getHasGretaAIChatSourceSwitched(sourceSwitchCount)

  const mainClassName = classNames(styles.main, {
    [styles.emptyChat]: getIsGretaAIChatSourceChoiceShown({
      isPinned,
      isProjectsPageShown,
      isSourceChoiceEnabled,
    }),
    [styles.finance]: isFinanceSource,
  })

  const cornerProps = isMinimized
    ? null
    : {
        ...(isExpanded
          ? null
          : { [GRETA_PANEL_COMPACT_ATTRIBUTE]: EMPTY_STRING }),
        [GRETA_PANEL_VISIBLE_ATTRIBUTE]: EMPTY_STRING,
      }

  function handleSelectChat(chatId) {
    if (chatId !== activeChatId || isProjectsPageShown) {
      onSelectChat(chatId)
    }

    onCloseChatList()
  }

  return (
    <section
      {...cornerProps}
      className={panelClassName}
      inert={isMinimized}
      ref={panelRef}
      style={GRETA_PANEL_STYLE_VARS}
      tabIndex={-1}
      onKeyDown={handleKeyDown}
    >
      <GretaAIChatHeader
        chatListToggleRef={chatListToggleRef}
        isChatListDocked={isChatListDocked}
        isExpanded={isExpanded}
        isLocked={isLocked}
        onMinimize={onMinimize}
        onToggleMode={handleToggleMode}
      />
      <div className={styles.body} ref={bodyRef}>
        {!isLocked && isChatListDocked && isChatListColumnOpen ? (
          <GretaAIChatChatListColumn
            resizeHandleProps={resizeHandleProps}
            style={chatListProps.style}
            onSelectChat={handleSelectChat}
          />
        ) : null}
        {!isLocked &&
        !isChatListDocked &&
        (isChatListPinned || isChatListHovered) ? (
          <GretaAIChatChatListDrawer
            isPinned={isChatListPinned}
            isRowMenuOpen={isChatListOrderFrozen}
            toggleRef={chatListToggleRef}
            onClose={onCloseChatList}
            onMouseEnter={onHoverChatList}
            onMouseLeave={onUnhoverChatList}
            onSelectChat={handleSelectChat}
          />
        ) : null}
        <div className={mainClassName}>
          {hasSourceSwitched ? (
            <GretaAIChatSourceSweep
              isFinance={isFinanceSource}
              key={sourceSwitchCount}
            />
          ) : null}
          {isProjectsPageShown ? (
            <GretaAIChatProjectsPage />
          ) : (
            <GretaAIChatPanelFeed inputRef={inputRef} />
          )}
          {isProjectsPageShown ? null : (
            <div className={styles.strip}>
              <GretaAIChatStripNotice
                isLimitReached={isLimitReached}
                isLocked={isLocked}
                isSourceOutage={isSourceOutage}
                isSourceRevoked={isSourceRevoked}
                onRetry={onRetry}
              />

              <GretaAIChatComposer inputRef={inputRef} />
            </div>
          )}
        </div>
      </div>
      <GretaAIChatPanelChatAction />
      <GretaAIChatPanelProjectAction />
    </section>
  )
}

export default GretaAIChatPanel
